import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { AIChatHeader } from './AIChatHeader';
import { AIChatMessages } from './AIChatMessages';
import { AIChatInput } from './AIChatInput';
import { useAIChat, type ProfileContext } from '@/hooks/useAIChat';
import { cn } from '@/lib/utils';

interface AIChatPanelProps {
  /** Profile being viewed in the feed */
  contextProfile?: {
    id: string;
    displayName: string;
    age?: number;
    bio?: string;
    lookingFor?: string;
    photos?: string[];
    occupation?: string;
  };
  /** Whether to auto-analyze photos on first message */
  analyzePhotos?: boolean;
}

export function AIChatPanel({ contextProfile, analyzePhotos = true }: AIChatPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Convert context profile to ProfileContext format
  const profileContext: ProfileContext | undefined = contextProfile
    ? {
        profileId: contextProfile.id,
        displayName: contextProfile.displayName,
        age: contextProfile.age,
        bio: contextProfile.bio,
        lookingFor: contextProfile.lookingFor,
        photos: contextProfile.photos?.map((url) => ({ url })),
        occupation: contextProfile.occupation,
      }
    : undefined;

  const {
    messages,
    isLoading,
    isStreaming,
    streamingContent,
    photoAnalysis,
    sendMessageStreaming,
    cancelStream,
    clearChat,
  } = useAIChat({
    contextType: contextProfile ? 'feed_profile' : 'general',
    contextProfileId: contextProfile?.id,
    analyzePhotos,
  });

  // Clear chat when profile changes
  useEffect(() => {
    if (contextProfile?.id) {
      clearChat();
    }
  }, [contextProfile?.id, clearChat]);

  const handleSend = (message: string) => {
    sendMessageStreaming(message);
  };

  return (
    <>
      {/* Floating AI button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed top-20 right-6 z-40 rounded-full w-14 h-14 shadow-lg',
          'bg-brand-primary hover:bg-brand-primary/90 text-white',
          'transition-all duration-300 hover:scale-105',
          isOpen && 'opacity-0 pointer-events-none'
        )}
      >
        <Sparkles className="w-6 h-6" />
      </Button>

      {/* Slide-in panel */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="right"
          className="w-full sm:w-[400px] lg:w-[450px] p-0 flex flex-col h-full"
        >
          {/* Hidden title for accessibility */}
          <SheetTitle className="sr-only">AI Dating Coach</SheetTitle>

          {/* Header */}
          <AIChatHeader
            profile={profileContext}
            photoAnalysis={photoAnalysis}
          />

          {/* Messages */}
          <AIChatMessages
            messages={messages}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
          />

          {/* Input */}
          <AIChatInput
            onSend={handleSend}
            onCancel={cancelStream}
            isLoading={isLoading}
            isStreaming={isStreaming}
            placeholder={
              contextProfile
                ? `Ask about ${contextProfile.displayName}...`
                : 'Ask me anything...'
            }
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
